<script>
import BaseHypothesis from '@/components/AttendanceForm/general/summary/BaseHypothesis.vue'
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

import { SYMPTOM_OPTIONS } from '@/components/AttendanceForm/atopicdermatitis/constants/symptom'
import atopicdermatitisScoresMixin from '@/components/AttendanceForm/atopicdermatitis/mixins/atopicdermatitisScoresMixin.js'
import { COMORBIDITIES_OPTIONS, CONTRACEPCAO } from '@/components/AttendanceForm/general/constants/personalHistory'
import { MEDICINE_CATEGORIES } from '@/constants/medicine'
import { HOSPITALIZATION_OPTIONS } from '../constants/hospitalization'

export default {
  mixins: [atopicdermatitisScoresMixin],
  extends: BaseHypothesis,
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey', 'attendance']),

    symptomsPhaseData() {
      return this.getPhaseDataByKey('sintomas')
    },

    medicinesPhaseData() {
      return this.getPhaseDataByKey('medicamentos')
    },

    previousTreatmentsPhaseData() {
      return this.getPhaseDataByKey('tratamentos_previos')
    },

    hospitalizationsPhaseData() {
      return this.getPhaseDataByKey('internacoes')
    },

    familyHistoryPhaseData() {
      return this.getPhaseDataByKey('investigacao_familiar')
    },

    investigationPhaseData() {
      return this.getPhaseDataByKey('investigacao_pessoal')
    },

    symptomsText() {
      const paragraphs = []

      if (!!this.symptomsPhaseData?.has_diagnosis?.specialty) {
        paragraphs.push(`Diagnóstico com: ${this.symptomsPhaseData.has_diagnosis?.specialty.specialtyName}`)
      }

      if (!!this.symptomsPhaseData?.has_diagnosis?.date) {
        paragraphs.push(
          `\nFeito no dia: ${this.$moment(this.symptomsPhaseData.has_diagnosis.date).format('DD/MM/YYYY')}`,
        )
      }
      paragraphs.push('\nApresenta os seguintes sintomas:')

      paragraphs.push('\n\tCritérios Maiores')

      const majorSymptoms = this.symptomsPhaseData?.symptoms.maiores.selected
        .filter((symptom) => symptom != 'atopia')
        .map((selectedSymptom) => SYMPTOM_OPTIONS.maiores.find((symptom) => symptom.key === selectedSymptom).name)
        .join(', ')

      paragraphs.push(`${majorSymptoms}`)

      paragraphs.push('\n')

      paragraphs.push('\n\tCritérios Menores')

      const facialSymptoms = this.symptomsPhaseData?.symptoms.menores.faciais.selected.map(
        (selectedSymptom) =>
          SYMPTOM_OPTIONS.menores[0].symptoms.find((symptom) => symptom.key === selectedSymptom).name,
      )

      if (facialSymptoms) facialSymptoms.forEach((symptom) => paragraphs.push(`\n\t\t${symptom}`))

      const triggers = this.symptomsPhaseData?.symptoms.menores.triggers.selected.map(
        (selectedSymptom) =>
          SYMPTOM_OPTIONS.menores[1].symptoms.find((symptom) => symptom.key === selectedSymptom).name,
      )

      if (triggers) {
        paragraphs.push('\n\tTriggers:')
        triggers.forEach((symptom) => paragraphs.push(`\n\t\t${symptom}`))
      }

      const complications = this.symptomsPhaseData?.symptoms.menores.complicacoes.selected.map(
        (selectedSymptom) =>
          SYMPTOM_OPTIONS.menores[2].symptoms.find((symptom) => symptom.key === selectedSymptom).name,
      )

      if (complications) {
        paragraphs.push('\n\tComplicações:')
        complications.forEach((symptom) => paragraphs.push(`\n\t\t${symptom}`))
      }

      const others = this.symptomsPhaseData?.symptoms.menores.outros.selected.map(
        (selectedSymptom) =>
          SYMPTOM_OPTIONS.menores[3].symptoms.find((symptom) => symptom.key === selectedSymptom).name,
      )

      if (others) {
        paragraphs.push('\n\tOutros:')
        others.forEach((symptom) => paragraphs.push(`\n\t\t${symptom}`))
      }

      return paragraphs.join('')
    },

    medicinesText() {
      const paragraphs = []
      if (this.medicinesPhaseData?.selectedTreatments) {
        const medicines = this.medicinesPhaseData?.selectedTreatments
          .map((treatment) => treatment.medicine.name)
          .join(', ')

        if (medicines)
          paragraphs.push(
            medicines.length > 0 ? `\nMedicamentos atuais: ${medicines}` : '\nNenhum medicamento prescrito.',
          )
      }

      return paragraphs.join('')
    },

    previousTreatmentsText() {
      const paragraphs = []

      if (this.previousTreatmentsPhaseData?.medicines) {
        const selectedTreatments = Object.keys(this.previousTreatmentsPhaseData.medicines || {})
          .map((key) => this.previousTreatmentsPhaseData.medicines[key])
          .sort(
            this.$util.objectSorter(
              (item) => item.medicine.category.name.toLowerCase() + item.medicine.name.toLowerCase(),
            ),
          )

        const currentTreatments = selectedTreatments
          .filter((medicine) => medicine.periods.length !== 0 && medicine.periods.some((p) => !!p.start && !p.end))
          .map((medicine) => this.formattedCurrentMedicineText(medicine))

        const previousAntihistamines = selectedTreatments
          .filter(
            (m) =>
              m.medicine.category.id === MEDICINE_CATEGORIES.ANTI_HISTAMINICOS &&
              (m.periods.length === 0 || m.periods.find((period) => period.end)),
          )
          .map((medicine) => this.formattedPreviousMedicineText(medicine))

        const previousImmunobiologicalTreatments = selectedTreatments
          .filter(
            (m) =>
              m.medicine.category.id === MEDICINE_CATEGORIES.IMUNOBIOLOGICOS &&
              (m.periods.length === 0 || m.periods.find((period) => period.end)),
          )
          .map((medicine) => this.formattedPreviousMedicineText(medicine))

        const previousTopicalTreatments = selectedTreatments
          .filter(
            (m) =>
              m.medicine.category.id === MEDICINE_CATEGORIES.TOPICOS &&
              (m.periods.length === 0 || m.periods.find((period) => period.end)),
          )
          .map((medicine) => this.formattedPreviousMedicineText(medicine))

        const previousSistemicTreatments = selectedTreatments
          .filter(
            (m) =>
              m.medicine.category.id === MEDICINE_CATEGORIES.SISTEMICOS_TRADICIONAIS &&
              (m.periods.length === 0 || m.periods.find((period) => period.end)),
          )
          .map((medicine) => this.formattedPreviousMedicineText(medicine))

        const previousMoisturizerTreatments = selectedTreatments
          .filter(
            (m) =>
              m.medicine.category.id === MEDICINE_CATEGORIES.HIDRATANTES &&
              (m.periods.length === 0 || m.periods.find((period) => period.end)),
          )
          .map((medicine) => this.formattedPreviousMedicineText(medicine))

        const previousAntibioticTreatments = selectedTreatments
          .filter(
            (m) =>
              m.medicine.category.id === MEDICINE_CATEGORIES.ANTIBIOTICOS &&
              (m.periods.length === 0 || m.periods.find((period) => period.end)),
          )
          .map((medicine) => this.formattedPreviousMedicineText(medicine))

        paragraphs.push(
          `\nHistórico de medicamentos: `,
          `\n- Medicamentos atuais: ${currentTreatments.length > 0 ? currentTreatments : 'nenhum'};`,
          `\n-  Anti-histamínicos prévios: ${previousAntihistamines.length > 0 ? previousAntihistamines : 'nenhum'};`,
          `\n- Imunobiológicos ou pequenas moléculas prévios: ${
            previousImmunobiologicalTreatments.length > 0 ? previousImmunobiologicalTreatments : 'nenhum'
          };`,
          `\n- Tópicos prévios: ${previousTopicalTreatments.length > 0 ? previousTopicalTreatments : 'nenhum'}.`,
          `\n- Sistêmicos tradicionais prévios: ${
            previousSistemicTreatments.length > 0 ? previousSistemicTreatments : 'nenhum'
          }.`,
          `\n- Hidratantes prévios: ${
            previousMoisturizerTreatments.length > 0 ? previousMoisturizerTreatments : 'nenhum'
          }.`,
          `\n- Antibióticos prévios: ${
            previousAntibioticTreatments.length > 0 ? previousAntibioticTreatments : 'nenhum'
          }.`,
        )
      }
      return paragraphs.join('')
    },

    hospitalizationText() {
      const paragraphs = []

      paragraphs.push('\nInternações:')

      this.hospitalizationsPhaseData?.hospitalizations.forEach((hospitalization, index) => {
        paragraphs.push(`\nInternação #${index + 1}`)

        const locations = Object.values(hospitalization.location)
          .filter((location) => location.selected)
          .map((location) => ({
            label: HOSPITALIZATION_OPTIONS.location.find((loc) => loc.key === location.selected).label,
            time: this.$moment(location.time.end).diff(location.time.start, 'days'),
          }))

        locations.forEach((location) =>
          paragraphs.push(
            `\n\tLocal de internação: ${location.label}, ${location.time} ${location.time > 1 ? 'dias' : 'dia'}`,
          ),
        )

        const reasons = hospitalization.reason.selected
          .filter((key) => key !== 'outros')
          .map((key) => HOSPITALIZATION_OPTIONS.reason.find((reason) => reason.key === key).label)
          .join(', ')

        const otherReasons = hospitalization.reason.other

        paragraphs.push(`\n\tMotivo principal da Internação: ${reasons}`)

        paragraphs.push('\n')
      })

      return paragraphs.join('')
    },

    familyHistoryText() {
      const paragraphs = []

      paragraphs.push('\nInvestigacão Familiar')

      if (this.familyHistoryPhaseData?.familyAtopias?.asma?.selected) {
        paragraphs.push(`\nAsma na família: ${this.familyHistoryPhaseData.familyAtopias.asma.selected ? 'Sim' : 'Não'}`)
        if (this.familyHistoryPhaseData.familyAtopias.asma.degree.length > 0) {
          paragraphs.push(
            `\n\tGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.asma.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }

      if (this.familyHistoryPhaseData?.familyAtopias?.rinite_alergica?.selected) {
        paragraphs.push(
          `\nRinite Alérgica na família: ${
            this.familyHistoryPhaseData.familyAtopias.rinite_alergica.selected ? 'Sim' : 'Não'
          }`,
        )
        if (this.familyHistoryPhaseData.familyAtopias.rinite_alergica.degree.length > 0) {
          paragraphs.push(
            `\n\tGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.rinite_alergica.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }

      if (this.familyHistoryPhaseData?.familyAtopias?.dermatite_atopica?.selected) {
        paragraphs.push(
          `\nDermatite Atopica na família: ${
            this.familyHistoryPhaseData.familyAtopias.dermatite_atopica.selected ? 'Sim' : 'Não'
          }`,
        )
        if (this.familyHistoryPhaseData.familyAtopias.dermatite_atopica.degree.length > 0) {
          paragraphs.push(
            `\n\tGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.dermatite_atopica.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }

      if (this.familyHistoryPhaseData?.familyAtopias?.psoriase?.selected) {
        paragraphs.push(
          `\nPsoríase na família: ${this.familyHistoryPhaseData.familyAtopias.psoriase.selected ? 'Sim' : 'Não'}`,
        )
        if (this.familyHistoryPhaseData.familyAtopias.psoriase.degree.length > 0) {
          paragraphs.push(
            `\n\tGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.psoriase.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }
      if (this.familyHistoryPhaseData?.profile) {
        if (this.familyHistoryPhaseData.profile['doenca_reumatologia']?.value) {
          paragraphs.push(
            `\nAlguém na família tem doença reumatológica (reumatismo, artrite, doença auto-imune): ${
              this.familyHistoryPhaseData.profile['doenca_reumatologia'].value ? 'Sim' : 'Não'
            }`,
          )
          if (this.familyHistoryPhaseData.profile['doenca_reumatologia'].complement) {
            paragraphs.push(
              `\nDoença informada: ${this.familyHistoryPhaseData.profile['doenca_reumatologia'].complement}`,
            )
          }
          if (this.familyHistoryPhaseData.profile['doenca_reumatologia'].degree) {
            paragraphs.push(
              `\n\tGrau de parentesco: ${this.familyHistoryPhaseData.profile['doenca_reumatologia'].degree}`,
            )
          }
        }

        if (this.familyHistoryPhaseData.profile['esclerose_multipla']?.value) {
          paragraphs.push(
            `\nAlguém na família tem esclerose mútlipla: ${
              this.familyHistoryPhaseData.profile['esclerose_multipla'].value ? 'Sim' : 'Não'
            }`,
          )
          if (this.familyHistoryPhaseData.profile['esclerose_multipla'].complement) {
            paragraphs.push(
              `\nDoença informada: ${this.familyHistoryPhaseData.profile['esclerose_multipla'].complement}`,
            )
          }
          if (this.familyHistoryPhaseData.profile['esclerose_multipla'].degree) {
            paragraphs.push(
              `\n\tGrau de parentesco: ${this.familyHistoryPhaseData.profile['esclerose_multipla'].degree}`,
            )
          }
        }

        if (this.familyHistoryPhaseData.profile['cancer_familia']?.value) {
          paragraphs.push(
            `\nAlgum histórico de câncer na família: ${
              this.familyHistoryPhaseData.profile['cancer_familia'].value ? 'Sim' : 'Não'
            }`,
          )
          if (this.familyHistoryPhaseData.profile['cancer_familia'].complement) {
            paragraphs.push(`\n\tDoença informada: ${this.familyHistoryPhaseData.profile['cancer_familia'].complement}`)
          }
          if (this.familyHistoryPhaseData.profile['cancer_familia'].degree) {
            paragraphs.push(`\n\tGrau de parentesco: ${this.familyHistoryPhaseData.profile['cancer_familia'].degree}`)
          }
        }
      }

      return paragraphs.join('')
    },

    comorbiditiesText() {
      if (
        !this.investigationPhaseData ||
        this.investigationPhaseData.noDisease ||
        !this.investigationPhaseData.diseases ||
        this.investigationPhaseData.diseases.length === 0
      ) {
        return null
      }

      const paragraphs = []

      paragraphs.push('\nInvestigação Pessoal')

      const diseases = (this.investigationPhaseData.diseases || [])
        .filter((key) => key !== 'outras_comorbidades')
        .map((key) => COMORBIDITIES_OPTIONS.find((comorbidity) => comorbidity.key === key))

      if (diseases.length > 0) {
        paragraphs.push(
          `\nApresenta as seguintes comorbidades: ${diseases.map((disease) => disease?.name).join(', ')}.`,
        )
      }
      if ((this.investigationPhaseData.otherDisease || '').trim()) {
        paragraphs.push(`\nOutras: ${this.investigationPhaseData.otherDisease.trim()}.`)
      }

      if ((this.investigationPhaseData.medicine || '').trim()) {
        paragraphs.push(`\nMedicamentos em uso: ${this.investigationPhaseData.medicine.trim()}.`)
      }

      if (this.investigationPhaseData.profile) {
        if (this.investigationPhaseData.profile['cancer_pele']?.value) {
          let text = '\nCâncer de pele:'
          const cancerSubtype = this.investigationPhaseData.profile['tipo_cancer_pele']?.value
          if (cancerSubtype) {
            if (['MELANOMA', 'CARCINOMA'].includes(cancerSubtype)) {
              text += ` Sim. (${cancerSubtype.toLowerCase()})`
            } else {
              text += ` Sim. (subtipo desconhecido)`
            }
          }
          paragraphs.push(`${text}.`)
        } else {
          paragraphs.push('\nCâncer de pele: Não.')
        }

        if (this.investigationPhaseData.profile['tabagista']?.value) {
          paragraphs.push(`\nÉ tabagista:  ${this.investigationPhaseData.profile['tabagista'].value ? 'Sim.' : 'Não'}`)
        }
        if (this.investigationPhaseData.profile['etilista']?.value) {
          paragraphs.push(`\nÉ etilista: ${this.investigationPhaseData.profile['etilista'].value ? 'Sim.' : 'Não'}`)
        }
        if (this.investigationPhaseData.profile['tuberculose']?.value) {
          paragraphs.push(
            `\nJá teve tuberculose: ${this.investigationPhaseData.profile['etilista'].value ? 'Sim.' : 'Não'}`,
          )
        }
        if (this.investigationPhaseData.profile['tuberculose_latente']?.value) {
          paragraphs.push(
            `\nJá tratou tuberculose latente, teve exposição prévia ou trabalha exposto a pessoas com possível tuberculose ativa: ${
              this.investigationPhaseData.profile['tuberculose_latente'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }
        if (this.investigationPhaseData.profile['tratou_tuberculose']?.value) {
          paragraphs.push(
            `\nTratou tuberculose doença ou tuberculose latente: ${
              this.investigationPhaseData.profile['tratou_tuberculose'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile['drogas_ilicitas']?.value) {
          paragraphs.push(
            `\nUsa drogas ilícita? ${this.investigationPhaseData.profile['drogas_ilicitas'].value ? 'Sim.' : 'Não.'}`,
          )
        }

        if (this.investigationPhaseData.profile['contracepcao']?.value) {
          paragraphs.push(
            `\nContracepção:  ${this.investigationPhaseData.profile['contracepcao'].value ? 'Sim.' : 'Não.'}`,
          )
        }

        if (this.investigationPhaseData.profile['diarreia_cronica']?.value) {
          paragraphs.push(
            `\nTem diarreia crônica, dor abdominal ou outras alterações de hábito intestinal. ${
              this.investigationPhaseData.profile['diarreia_cronica'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile['uveite']?.value) {
          paragraphs.push(
            `\nTem olhos vermelhos, fotofobia, lacrimejamento ou outras alterações visuais (uveíte) ${
              this.investigationPhaseData.profile['uveite'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile['gravidez']?.value) {
          paragraphs.push(
            `\nEstá grávida ou pretende engravidar durante o tratamento? ${
              this.investigationPhaseData.profile['gravidez'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile_info['contracepcao']?.selected) {
          const birthcontrolOptions = this.investigationPhaseData.profile_info['contracepcao'].selected.map(
            (key) => CONTRACEPCAO.find((birthcontrol) => birthcontrol.key === key).name,
          )

          birthcontrolOptions.join(',  ')

          paragraphs.push(`\nMétodos Contraceptivos: ${birthcontrolOptions}`)
        }

        if (this.investigationPhaseData.profile_info['menarca']?.age) {
          paragraphs.push(`\nMenarca com quantos anos? ${this.investigationPhaseData.profile_info['menarca'].age}`)
        }
      }
      return paragraphs.join('')
    },
  },
  methods: {
    getHypothesisText() {
      const paragraphs = []

      paragraphs.push(this.symptomsText)

      paragraphs.push(this.medicinesText)

      paragraphs.push(this.previousTreatmentsText)

      paragraphs.push(this.hospitalizationText)

      paragraphs.push(this.comorbiditiesText)

      paragraphs.push(this.familyHistoryText)

      return paragraphs.join('\n')
    },

    formattedCurrentMedicineText(medicine) {
      return `${medicine.medicine.name.firstLetterToLowercase()}${
        medicine.periods.some((p) => !!p.start && !p.end)
          ? ' (desde ' + this.$util.toFormattedDate(medicine.periods.find((p) => !p.end).start) + ')'
          : ''
      }`
    },

    formattedPreviousMedicineText(medicine) {
      return `${medicine.medicine.name.firstLetterToLowercase()}`
    },
  },
}
</script>

<style scoped></style>
